import React from 'react';
import PropTypes from 'prop-types';
import VisitCards from './VisitCards';
import * as useFetchVisitsModule from './useFetchVisits';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Mock Data
const generateMockVisits = (count) => {
  const visits = [];
  for (let i = 1; i <= count; i += 1) {
    visits.push({
      id: i,
      date: `2024-11-${String(i).padStart(2, '0')}`,
      site_name: `Site ${i}`,
      completed: i % 2 === 0,
      purpose: i % 3 === 0 ? 'Routine Visit' : 'Follow-Up Visit',
      leader: `Leader ${i}`,
    });
  }
  return visits;
};

const mockVisits = generateMockVisits(9);

// Mock useFetchVisits
// eslint-disable-next-line no-import-assign, import/namespace
useFetchVisitsModule.default = () => {
  return mockVisits;
};

export default {
  title: 'Components/Visits/VisitCards',
  component: VisitCards,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `VisitCards` component displays visit records in a visually appealing card format. It enables users to review visit details in a gallery-style layout.\n\n',
      // When & how to use it?
      '\nUse the `VisitCards` component when a card/gallery-style representation of visit records is needed. It provides a responsive grid layout to display multiple visit cards.\n\n',
      // Current Usage
      '\nThe `VisitCards` component is currently used on the `VisitsPage` when the user switches to the cards/gallery view. It allows users to review visit details in a grid layout.\n\n',
      // Related Components
      `\n- **[VisitCard](http://localhost:6006/?path=/docs/components-visits-visitcard--docs)**: Represents individual cards within the VisitCards component.
       \n- **useFetchVisits** (app/javascript/src/componentsV2/Visits/useFetchVisits.js): Custom hook for fetching visit data.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'Organization ID used to fetch visit records for the cards.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    params: {
      control: { type: 'object' },
      description: 'Search and filter parameters for fetching data dynamically.\n\n',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    currentPage: {
      control: { type: 'number' },
      description: 'Current page number for paginated data.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    itemsPerPage: {
      control: { type: 'number' },
      description: 'Number of items displayed per page.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    setTotalVisits: {
      control: { type: 'function' },
      description: 'Callback function to set the total number of visits for pagination.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

const DEFAULT_VISITS = [];

const VisitCardsTemplate = ({
  visits = DEFAULT_VISITS,
  isLoading = false,
}) => (
  <VisitCards
    visits={visits}
    isLoading={isLoading}
  />
);

VisitCardsTemplate.propTypes = {
  visits: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const Template = VisitCardsTemplate.bind({});

export const DefaultVisitCards = Template.bind({});
DefaultVisitCards.args = {
  visits: mockVisits,
  isLoading: false,
};

DefaultVisitCards.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when the user toggles to cards/gallery view on the `VisitsPage` to display visit records in a grid layout. In Storybook, dummy data is displayed for component review purposes because API calls are not possible.\n\n',
  // Where is it currently used on the product?
  '\nThe `VisitCards` component is currently used on the VisitsPage for displaying visit records in a card/gallery format.\n\n',
);
